/**
 * The rail as a drawer, for viewports too narrow to dock it.
 *
 * Below finixt-ui's compact breakpoint the rail leaves AppLayout and renders
 * here, in a portal over a scrim, at the rail's own width. Radix Dialog
 * supplies the trap, Escape, and the scrim click; a route change closes it,
 * so a tapped row dismisses the drawer it was in.
 *
 * The restore to the opener is the layer's own, since the opener is a header
 * button outside the dialog and not a Radix Trigger.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useReturnFocus } from './useReturnFocus.js';

const SCRIM_Z_INDEX = 80;

const HIDDEN_TITLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name of the drawer. */
  label: string;
  /** The rail. */
  children: ReactNode;
}

/** The drawer. */
export function MobileDrawer({ open, onOpenChange, label, children }: MobileDrawerProps) {
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);
  const onCloseAutoFocus = useReturnFocus(open);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (open) onOpenChange(false);
  }, [pathname, open, onOpenChange]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: SCRIM_Z_INDEX,
            background: 'var(--scrim)',
            animation: 'fadein var(--dur-fast) var(--ease-out)',
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            zIndex: SCRIM_Z_INDEX + 1,
            width: 'var(--w-app-rail)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface-card)',
            outline: 'none',
            animation: 'ui-drawer-in var(--dur-slide) var(--ease-out)',
          }}
        >
          <Dialog.Title style={HIDDEN_TITLE}>{label}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
